import React from 'react';
import { Users, Plus, X, Edit2, Trash2, Phone, Mail, MapPin } from 'lucide-react';
import type { Database } from '../../lib/database.types';
import { useTechnicians } from '../../lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface TechniciansTabProps {
  onAddTechnician: () => void;
  onEditTechnician: (technician: Database['public']['Tables']['technicians']['Row']) => void;
}

const TechniciansTab: React.FC<TechniciansTabProps> = ({ onAddTechnician, onEditTechnician }) => {
  const { technicians, loading: technicianLoading, deleteTechnician } = useTechnicians();

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium">Technicians</h2>
          <p className="text-muted-foreground">Manage your service technicians</p>
        </div>
        <Button onClick={onAddTechnician}>
          <Plus className="w-5 h-5 mr-2" />
          Add Technician
        </Button>
      </div>

      {technicianLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-4 w-[150px]" />
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : technicians.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <CardTitle className="text-lg mb-2">No technicians yet</CardTitle>
            <CardDescription className="mb-6">
              Get started by adding a new technician
            </CardDescription>
            <Button onClick={onAddTechnician}>
              <Plus className="w-5 h-5 mr-2" />
              Add Technician
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {technicians.map((technician) => (
            <Card
              key={technician.id}
              className="group hover:shadow-md transition-shadow"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Avatar>
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {technician.full_name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-lg">
                        {technician.full_name}
                      </CardTitle>
                      <div className="mt-2 space-y-1">
                        {technician.email && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Mail className="w-4 h-4 mr-2" />
                            <a href={`mailto:${technician.email}`} className="hover:text-primary">
                              {technician.email}
                            </a>
                          </div>
                        )}
                        {technician.phone && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Phone className="w-4 h-4 mr-2" />
                            <a href={`tel:${technician.phone}`} className="hover:text-primary">
                              {technician.phone}
                            </a>
                          </div>
                        )}
                        {technician.address && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <MapPin className="w-4 h-4 mr-2" />
                            <span>
                              {technician.address}, {technician.city}, {technician.state} {technician.zip_code}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditTechnician(technician)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this technician?')) {
                          deleteTechnician(technician.id);
                        }
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default TechniciansTab;